/**
 * Le schéma d'une RÉPONSE SUR UN SOLIDE (atelier de figures, lot L6, F10 ;
 * `fiches/solide-manipule.ts`). La forme seulement : ce que le schéma ne voit
 * pas (une arête qui n'existe pas, un patron qui ne se complète pas) est dit
 * par `griefsTacheSolide`, à l'import.
 */
import { z } from 'zod';
import { QUOI_MARQUER, REPERES_MARQUABLES, canoniserMarque, completionExiste, estManipulable, nomDElement, patronDonne, polyedreNomme, } from '../fiches/solide-manipule.js';
/** MARQUER : désigner un sommet, une arête, une face, ou un segment (une arête, la hauteur, l'apothème). */
export const tacheMarquerSchema = z
    .object({
    type: z.literal('marquer'),
    quoi: z.enum(QUOI_MARQUER),
    /** Les segments remarquables d'une pyramide, dessinés en pointillé parmi les candidats. */
    reperes: z.array(z.enum(REPERES_MARQUABLES)).min(1).max(2).optional(),
    /** L'élément demandé (« A-E », « ABFE », « hauteur ») — ou plusieurs, tous justes. */
    attendu: z.union([z.string().trim().min(1), z.array(z.string().trim().min(1)).min(1).max(12)]),
})
    .strict();
/**
 * DÉVELOPPER : compléter un patron. Le patron donné part d'une face (`depart`)
 * et déplie, dans l'ordre, la face voisine de chaque arête de `plis`.
 * `erreurs` : les erreurs du catalogue que le code nomme (un ALIAS dans le
 * dossier) quand une face bute au pliage, quand deux faces se chevauchent à plat.
 */
export const tacheDevelopperSchema = z
    .object({
    type: z.literal('developper'),
    depart: z.string().trim().min(1),
    plis: z.array(z.string().trim().min(1)).max(10),
    erreurs: z
        .object({
        recouvrement: z.string().min(1).optional(),
        chevauchement: z.string().min(1).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const tacheSolideSchema = z.discriminatedUnion('type', [
    tacheMarquerSchema,
    tacheDevelopperSchema,
]);
/** La vue de départ d'un solide qu'on tourne, en degrés. */
export const vueSolideSchema = z
    .object({ azimut: z.number().finite(), elevation: z.number().finite().min(-89).max(89) })
    .strict();
/** Les éléments attendus d'une marque, en liste. */
export const attendusDeMarque = (t) => typeof t.attendu === 'string' ? [t.attendu] : t.attendu;
/** La sorte d'un élément, par son identifiant canonique. */
function sorteDe(p, id) {
    if (p.sommets.has(id))
        return 'sommet';
    if (p.aretes.includes(id))
        return 'arete';
    if (p.faces.some((f) => f.id === id))
        return 'face';
    if (p.reperes.has(id))
        return 'repere';
    return null;
}
const ADMISES = {
    sommet: ['sommet'],
    arete: ['arete'],
    face: ['face'],
    segment: ['arete', 'repere'],
};
/**
 * CE QUE L'IMPORT REFUSE d'une tâche sur un solide (F10) :
 *  - un solide qui n'est pas un polyèdre manipulable (cube, pavé, prisme
 *    triangulaire, pyramide) ;
 *  - MARQUER : un attendu qui n'est pas un élément du solide, ou pas de la
 *    sorte demandée ; des repères hors d'une pyramide ou hors d'un segment ;
 *    un repère attendu qui n'est pas dessiné ;
 *  - DÉVELOPPER : un patron donné mal plié (`patronDonne`), déjà complet (rien
 *    à faire), ou qu'aucune suite de faces ne referme sans recouvrement.
 */
export function griefsTacheSolide(solide, tache) {
    if (!estManipulable(solide)) {
        return [
            `un « ${solide.forme} » ne se manipule pas ici : un cube, un pavé, un prisme triangulaire ou une pyramide`,
        ];
    }
    const p = polyedreNomme(solide);
    const griefs = [];
    if (tache.type === 'marquer') {
        if (tache.reperes !== undefined) {
            if (solide.forme !== 'pyramide')
                griefs.push('la hauteur et l’apothème se marquent sur une pyramide');
            if (tache.quoi !== 'segment')
                griefs.push('les repères se désignent parmi des segments (`quoi: "segment"`)');
        }
        for (const brut of attendusDeMarque(tache)) {
            const id = canoniserMarque(p, brut);
            const sorte = id === null ? null : sorteDe(p, id);
            if (id === null || sorte === null) {
                griefs.push(`« ${brut} » n’est pas un élément du solide`);
                continue;
            }
            if (!ADMISES[tache.quoi].includes(sorte)) {
                griefs.push(`« ${brut} » (${nomDElement(p, id) ?? brut}) n’est pas un ${tache.quoi} à désigner`);
            }
            if (sorte === 'repere' && !(tache.reperes ?? []).includes(id)) {
                griefs.push(`${nomDElement(p, id) ?? id} est attendue, mais n’est pas dessinée (\`reperes\`)`);
            }
        }
        return griefs;
    }
    const r = patronDonne(p, tache.depart, tache.plis);
    if ('griefs' in r)
        return [...r.griefs];
    if (new Set(r.patron.map((f) => f.face)).size === p.faces.length) {
        griefs.push('le patron donné est déjà complet : il ne reste rien à attacher');
    }
    else if (!completionExiste(p, r.patron)) {
        griefs.push('aucune suite de faces ne referme ce patron sans recouvrement');
    }
    return griefs;
}
//# sourceMappingURL=solide-manipule.schema.js.map